import { useState, useEffect, useMemo } from "react";

interface UseSellerFormProps {
  serverErrors?: any;
}

export const useSellerForm = (props?: UseSellerFormProps) => {
  const [formData, setFormData] = useState({
    institutionName: "",
    shortBio: "",
    typeOfUser: [] as string[],
    generalFocus: [] as string[],
    links: {
      websiteLink: "",
      portfolioLink: "",
      twitterLink: "",
      facebookLink: "",
      instagramLink: "",
    },
    termsAndConditions: false,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Track if fields have been modified to hide errors on typing (like auth pages)
  const [institutionNameTouched, setInstitutionNameTouched] = useState<boolean>(false);
  const [shortBioTouched, setShortBioTouched] = useState<boolean>(false);
  const [typeOfUserTouched, setTypeOfUserTouched] = useState<boolean>(false);
  const [generalFocusTouched, setGeneralFocusTouched] = useState<boolean>(false);
  const [linksTouched, setLinksTouched] = useState<boolean>(false);
  const [termsAndConditionsTouched, setTermsAndConditionsTouched] = useState<boolean>(false);

  // Individual link field touched states
  const [websiteLinkTouched, setWebsiteLinkTouched] = useState<boolean>(false);
  const [portfolioLinkTouched, setPortfolioLinkTouched] = useState<boolean>(false);
  const [twitterLinkTouched, setTwitterLinkTouched] = useState<boolean>(false);
  const [facebookLinkTouched, setFacebookLinkTouched] = useState<boolean>(false);
  const [instagramLinkTouched, setInstagramLinkTouched] = useState<boolean>(false);

  // Parse server errors to field-specific errors (like other components in the app)
  const parseServerErrors = (error: any) => {
    const fieldErrors: { [key: string]: string } = {};
    
    if (!error) return fieldErrors;
    
    // Check if error has field-specific validation errors
    if (typeof error === 'object' && error) {
      // Handle validation errors object
      if (error.errors) {
        Object.keys(error.errors).forEach(field => {
          const fieldError = error.errors[field];
          if (typeof fieldError === 'string') {
            fieldErrors[field] = fieldError;
          } else if (fieldError.message) {
            fieldErrors[field] = fieldError.message;
          } else if (Array.isArray(fieldError)) {
            fieldErrors[field] = fieldError[0]; // Take first error if array
          }
        });
      }
      
      // Handle response.data structure (if error is nested in response)
      if (error.response && error.response.data) {
        return parseServerErrors(error.response.data);
      }
      
      // Handle direct field errors for SellerForm fields
      if (error.institutionName) fieldErrors.institutionName = typeof error.institutionName === 'string' ? error.institutionName : error.institutionName.message || 'Invalid institution name';
      if (error.shortBio) fieldErrors.shortBio = typeof error.shortBio === 'string' ? error.shortBio : error.shortBio.message || 'Invalid short bio';
      if (error.typeOfUser) fieldErrors.typeOfUser = typeof error.typeOfUser === 'string' ? error.typeOfUser : error.typeOfUser.message || 'Invalid type of user selection';
      if (error.generalFocus) fieldErrors.generalFocus = typeof error.generalFocus === 'string' ? error.generalFocus : error.generalFocus.message || 'Invalid general focus selection';
      
      // Handle individual link field errors
      if (error.websiteLink) fieldErrors.websiteLink = typeof error.websiteLink === 'string' ? error.websiteLink : error.websiteLink.message || 'Invalid website URL';
      if (error.portfolioLink) fieldErrors.portfolioLink = typeof error.portfolioLink === 'string' ? error.portfolioLink : error.portfolioLink.message || 'Invalid portfolio URL';
      if (error.twitterLink) fieldErrors.twitterLink = typeof error.twitterLink === 'string' ? error.twitterLink : error.twitterLink.message || 'Invalid Twitter URL';
      if (error.facebookLink) fieldErrors.facebookLink = typeof error.facebookLink === 'string' ? error.facebookLink : error.facebookLink.message || 'Invalid Facebook URL';
      if (error.instagramLink) fieldErrors.instagramLink = typeof error.instagramLink === 'string' ? error.instagramLink : error.instagramLink.message || 'Invalid Instagram URL';
      
      // Handle legacy link field names for backward compatibility
      if (error.website) fieldErrors.websiteLink = typeof error.website === 'string' ? error.website : error.website.message || 'Invalid website URL';
      if (error.portfolio) fieldErrors.portfolioLink = typeof error.portfolio === 'string' ? error.portfolio : error.portfolio.message || 'Invalid portfolio URL';
      if (error.twitter) fieldErrors.twitterLink = typeof error.twitter === 'string' ? error.twitter : error.twitter.message || 'Invalid Twitter URL';
      if (error.facebook) fieldErrors.facebookLink = typeof error.facebook === 'string' ? error.facebook : error.facebook.message || 'Invalid Facebook URL';
      if (error.instagram) fieldErrors.instagramLink = typeof error.instagram === 'string' ? error.instagram : error.instagram.message || 'Invalid Instagram URL';
      
      // Handle general links error (fallback)
      if (error.links && !fieldErrors.websiteLink && !fieldErrors.portfolioLink && !fieldErrors.twitterLink && !fieldErrors.facebookLink && !fieldErrors.instagramLink) {
        fieldErrors.links = typeof error.links === 'string' ? error.links : error.links.message || 'Invalid link provided';
      }
      
      // Handle msg field (common in your app)
      if (error.msg && !Object.keys(fieldErrors).length) {
        fieldErrors.general = error.msg;
      }
    }
    
    // If it's a string error, try to parse as JSON
    if (typeof error === 'string') {
      try {
        const parsed = JSON.parse(error);
        return parseServerErrors(parsed);
      } catch {
        // If not JSON, treat as general error
        fieldErrors.general = error;
      }
    }
    
    return fieldErrors;
  };

  // Merge server errors with client validation errors
  const serverErrors = useMemo(
    () => (props?.serverErrors ? parseServerErrors(props.serverErrors) : {}),
    // Only recompute when the incoming serverErrors prop changes
    [props?.serverErrors]
  );
  const allErrors = useMemo(() => ({ ...errors, ...serverErrors }), [errors, serverErrors]);
  
  // Debug logging
  if (props?.serverErrors) {
    console.log("Raw server errors:", props.serverErrors);
    console.log("Parsed server errors:", serverErrors);
    console.log("All errors (merged):", allErrors);
  }

  // Parse errors for field-specific display (exactly like auth pages)
  const getFieldError = (fieldName: string) => {
    if (!allErrors || Object.keys(allErrors).length === 0) return null;
    
    // Hide errors if user has started typing in the field
    if (fieldName === 'institutionName' && institutionNameTouched) return null;
    if (fieldName === 'shortBio' && shortBioTouched) return null;
    if (fieldName === 'typeOfUser' && typeOfUserTouched) return null;
    if (fieldName === 'generalFocus' && generalFocusTouched) return null;
    if (fieldName === 'links' && linksTouched) return null;
    if (fieldName === 'termsAndConditions' && termsAndConditionsTouched) return null;
    
    // Hide individual link field errors if user has started typing
    if (fieldName === 'websiteLink' && websiteLinkTouched) return null;
    if (fieldName === 'portfolioLink' && portfolioLinkTouched) return null;
    if (fieldName === 'twitterLink' && twitterLinkTouched) return null;
    if (fieldName === 'facebookLink' && facebookLinkTouched) return null;
    if (fieldName === 'instagramLink' && instagramLinkTouched) return null;
    
    // Return the specific field error if it exists (server errors take precedence)
    const fieldError = allErrors[fieldName];
    if (fieldError) {
      return typeof fieldError === 'string' ? fieldError : String(fieldError);
    }
    
    return null;
  };

  // Reset touched states when new errors come in (like auth pages)
  useEffect(() => {
    if ((errors && Object.keys(errors).length > 0) || (serverErrors && Object.keys(serverErrors).length > 0)) {
      setInstitutionNameTouched(false);
      setShortBioTouched(false);
      setTypeOfUserTouched(false);
      setGeneralFocusTouched(false);
      setLinksTouched(false);
      setTermsAndConditionsTouched(false);
      // Reset individual link field touched states
      setWebsiteLinkTouched(false);
      setPortfolioLinkTouched(false);
      setTwitterLinkTouched(false);
      setFacebookLinkTouched(false);
      setInstagramLinkTouched(false);
    }
  }, [errors, serverErrors]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    const { name } = target as any;
    const isCheckbox = (target as HTMLInputElement).type === 'checkbox';
    const value = isCheckbox ? (target as HTMLInputElement).checked : target.value;

    // Set touched state for the field being changed
    if (name === 'institutionName') setInstitutionNameTouched(true);
    if (name === 'shortBio') setShortBioTouched(true);
    if (name.startsWith('links.')) {
      setLinksTouched(true);
      // Set individual link field touched states
      if (name === 'links.websiteLink') setWebsiteLinkTouched(true);
      if (name === 'links.portfolioLink') setPortfolioLinkTouched(true);
      if (name === 'links.twitterLink') setTwitterLinkTouched(true);
      if (name === 'links.facebookLink') setFacebookLinkTouched(true);
      if (name === 'links.instagramLink') setInstagramLinkTouched(true);
    }
    if (name === 'termsAndConditions') setTermsAndConditionsTouched(true);

    setFormData((prev) => {
      if (name.startsWith("links.")) {
        const key = name.split(".")[1];
        return {
          ...prev,
          links: { ...prev.links, [key]: value },
        };
      } else {
        return {
          ...prev,
          [name]: value,
        };
      }
    });
  };

  const handleCheckboxChange = (category: "typeOfUser" | "generalFocus", value: string) => {
    // Set touched state for the field being changed
    if (category === 'typeOfUser') setTypeOfUserTouched(true);
    if (category === 'generalFocus') setGeneralFocusTouched(true);

    setFormData((prev) => {
      const newSet = new Set(prev[category]);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return { ...prev, [category]: Array.from(newSet) };
    });
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Add validation for Institution Name
    if (!formData.institutionName.trim()) {
      newErrors.institutionName = "Institution Name is required.";
    }

    if (!formData.shortBio.trim()) {
      newErrors.shortBio = "Short Bio is required.";
    }

    // Add validation for type of user selection
    if (formData.typeOfUser.length === 0) {
      newErrors.typeOfUser = "Please select at least one type of user.";
    }

    // Add validation for general focus selection
    if (formData.generalFocus.length === 0) {
      newErrors.generalFocus = "Please select at least one general focus area.";
    }

    const hasAtLeastOneLink = Object.values(formData.links).some((link) => link.trim() !== "");
    if (!hasAtLeastOneLink) {
      newErrors.links = "You must provide at least one website or social media profile.";
    }

    if (!formData.termsAndConditions) {
      newErrors.termsAndConditions = "You must agree to the terms and conditions.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const submitForm = () => {
    if(validateForm()) {
      return formData;
    }
    return {};
  }
  
  return { 
    formData, 
    handleInputChange, 
    handleCheckboxChange, 
    validateForm, 
    submitForm, 
    errors,
    getFieldError // Export the getFieldError function
  };
};
